import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { GET_JIRA_STATUS, GET_USER_ACTIONS, SET_JIRA_AUTOMATIC_SYNC } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";

const actionBtn =
  "px-2.5 py-1.5 border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer flex items-center gap-1 transition-colors font-normal hover:border-accent hover:text-text hover:bg-bg3";

const actionBtnPrimary =
  "!bg-accent !text-white !border-accent font-semibold hover:!bg-accent2 hover:!border-accent2";

interface UserActionEntry {
  id: number;
  ts: string;
  user_id: string;
  user_role: string;
  project_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  summary: string;
}

interface ServerVersion {
  commit_hash: string;
  deployed_at: string;
}

export function Settings() {
  const { authRole, authUserId, impersonatedRole, setImpersonatedRole } = useApp();
  const isAdmin = authRole === "admin";

  const [theme, setTheme] = useState<"dark" | "light">(() => {
    try {
      return (localStorage.getItem("argus-theme") as "dark" | "light") || "dark";
    } catch {
      return "dark";
    }
  });
  const [serverVersion, setServerVersion] = useState<ServerVersion | null>(null);
  const [jiraSyncMessage, setJiraSyncMessage] = useState("");
  const { data: jiraStatusData, refetch: refetchJiraStatus } = useQuery<any>(GET_JIRA_STATUS);
  const [setJiraAutomaticSync, { loading: jiraSyncSaving }] = useMutation<any>(SET_JIRA_AUTOMATIC_SYNC);
  const jiraStatus = jiraStatusData?.jiraStatus;

  const toggleJiraAutomaticSync = async () => {
    const enabled = !jiraStatus?.schedulingEnabled;
    setJiraSyncMessage("");
    try {
      await setJiraAutomaticSync({ variables: { enabled } });
      await refetchJiraStatus();
      setJiraSyncMessage(`Automatic Jira sync ${enabled ? "enabled" : "disabled"} for staging.`);
    } catch (error) {
      setJiraSyncMessage(error instanceof Error ? error.message : "Could not update automatic Jira sync.");
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem("argus-theme", theme);
    } catch { /* noop in test env */ }
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    let cancelled = false;
    fetch("/version")
      .then(response => response.ok ? response.json() : null)
      .then((data: ServerVersion | null) => {
        if (!cancelled) setServerVersion(data);
      })
      .catch(() => {
        if (!cancelled) setServerVersion(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: actionsRaw } = useQuery<any>(GET_USER_ACTIONS, {
    variables: { limit: 50, user: authUserId },
    skip: !authUserId,
  });
  const actions: UserActionEntry[] = actionsRaw ? mapToSnakeCase(actionsRaw.userActions) : [];

  return (
    <div className="flex-1 px-7 py-6 overflow-y-auto" data-testid="settings-view">
      <h1 className="text-xl font-semibold text-text mb-4">Settings</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Theme */}
        <div className="bg-bg2 border border-border rounded-default p-5">
          <h2 className="text-sm font-semibold text-text mb-2">Theme</h2>
          <div className="flex gap-2 mt-3">
            <button
              className={`${actionBtn}${theme === "dark" ? ` ${actionBtnPrimary}` : ""}`}
              onClick={() => setTheme("dark")}
            >
              Dark
            </button>
            <button
              className={`${actionBtn}${theme === "light" ? ` ${actionBtnPrimary}` : ""}`}
              onClick={() => setTheme("light")}
            >
              Light
            </button>
          </div>
        </div>

        {/* Server version */}
        <div className="bg-bg2 border border-border rounded-default p-5" data-testid="server-version">
          <h2 className="text-sm font-semibold text-text mb-2">Server Version</h2>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs mt-3">
            <span className="text-text2">Commit</span>
            <code className="text-text font-mono break-all">{serverVersion?.commit_hash || "unknown"}</code>
            <span className="text-text2">Deployed</span>
            <span className="text-text">{formatVersionDate(serverVersion?.deployed_at)}</span>
          </div>
        </div>
      </div>

      {/* Impersonation (admin only) */}
      {isAdmin && (
        <div className="bg-bg2 border border-border rounded-default p-5 mb-4">
          <h2 className="text-sm font-semibold text-text mb-2">Role Impersonation</h2>
          <p className="text-xs text-text2 mb-3">
            Test the application as a different user role. This overrides your permissions throughout the UI without affecting the server.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              className={`${actionBtn}${!impersonatedRole ? ` ${actionBtnPrimary}` : ""}`}
              onClick={() => setImpersonatedRole(null)}
            >
              Admin (default)
            </button>
            <button
              className={`${actionBtn}${impersonatedRole === "manager" ? ` ${actionBtnPrimary}` : ""}`}
              onClick={() => setImpersonatedRole("manager")}
            >
              Manager
            </button>
            <button
              className={`${actionBtn}${impersonatedRole === "lead" ? ` ${actionBtnPrimary}` : ""}`}
              onClick={() => setImpersonatedRole("lead")}
            >
              Lead
            </button>
            <button
              className={`${actionBtn}${impersonatedRole === "viewer" ? ` ${actionBtnPrimary}` : ""}`}
              onClick={() => setImpersonatedRole("viewer")}
            >
              Viewer
            </button>
          </div>
          {impersonatedRole && (
            <div className="mt-3 px-3 py-2 bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-default text-xs text-[var(--accent)]">
              Currently impersonating: <strong>{impersonatedRole}</strong>
            </div>
          )}
        </div>
      )}

      {isAdmin && jiraStatus?.schedulingControlAvailable && (
        <div className="bg-bg2 border border-border rounded-default p-5 mb-4" data-testid="jira-auto-sync-settings">
          <h2 className="text-sm font-semibold text-text mb-2">Staging Jira Synchronization</h2>
          <p className="text-xs text-text2 mb-3">
            Allow the staging scheduler to update linked Jira tickets every 10 minutes. When disabled, scheduled calls stop before contacting Jira.
          </p>
          <button
            type="button"
            className={`${actionBtn}${jiraStatus.schedulingEnabled ? "" : ` ${actionBtnPrimary}`}`}
            onClick={toggleJiraAutomaticSync}
            disabled={jiraSyncSaving}
          >
            {jiraSyncSaving ? "Saving..." : jiraStatus.schedulingEnabled ? "Disable automatic sync" : "Enable automatic sync"}
          </button>
          {jiraSyncMessage && <div className="text-xs text-text2 mt-3" role="status">{jiraSyncMessage}</div>}
        </div>
      )}

      {/* Feedback */}
      <div className="bg-bg2 border border-border rounded-default p-5 mb-4">
        <h2 className="text-sm font-semibold text-text mb-2">Feedback</h2>
        <p className="text-xs text-text2 mb-3">
          Want to report a bug or suggest a feature? Create a ticket on the ArgusTrace Jira backlog and it will be triaged by the team.
        </p>
        <a
          href="https://fptech.atlassian.net/jira/software/c/projects/AGTR/boards/2207/backlog"
          target="_blank"
          rel="noopener noreferrer"
          className={`${actionBtn} ${actionBtnPrimary} inline-flex no-underline`}
        >
          Report a Bug or Suggest a Feature
        </a>
      </div>

      {/* Audit Log */}
      <div className="bg-bg2 border border-border rounded-default p-5">
        <h2 className="text-sm font-semibold text-text mb-3">My Activity</h2>
        <p className="text-xs text-text2 mb-3">Your recent actions (30-day retention, immutable)</p>
        {actions.length === 0 ? (
          <div className="text-xs text-text2 py-3">No actions recorded yet.</div>
        ) : (
          <div className="overflow-auto max-h-80">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wide text-text2 border-b border-border font-semibold">Time</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wide text-text2 border-b border-border font-semibold">User</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wide text-text2 border-b border-border font-semibold">Action</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wide text-text2 border-b border-border font-semibold">Summary</th>
                </tr>
              </thead>
              <tbody>
                {actions.map(a => (
                  <tr key={a.id} className="border-b border-border/50">
                    <td className="px-2 py-1.5 text-text2 whitespace-nowrap">{formatTs(a.ts)}</td>
                    <td className="px-2 py-1.5 text-text">{a.user_id}</td>
                    <td className="px-2 py-1.5 text-accent">{a.action}</td>
                    <td className="px-2 py-1.5 text-text">{a.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatVersionDate(iso?: string): string {
  if (!iso) return "unknown";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
